import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// Plays-once-per-session homepage intro. Three phases over 4.14s:
//   0–1344ms    logo slides up from below the viewport, decelerating into
//                place; motion blur clears (6px → 0px) as it settles
//   1344–2744ms hold (logo fully visible for 1.4s)
//   2744–4144ms iris-reveal: the white splash develops a circular transparent
//                hole that grows from 0% to 120% of viewport — the homepage
//                shows through the hole. NO black phase. Logo + white fade
//                away together via the mask. At animation end the component
//                unmounts and dispatches a 'intro:complete' window event so
//                the hero video can start (HeroSection.jsx listens, waits
//                300ms, then plays). Returning visitors bail at the top of
//                the component and the video autoplays normally.
//
// IMAGE PRELOAD GATE: same as before — the framer animations are not
// rendered until the logo PNG is downloaded, with a 2500ms defensive cap.
// See EDITS.md > Known follow-ups for the PNG-size optimization.

const INTRO_SESSION_KEY = 'openbooq_intro_shown';
const INTRO_COMPLETE_EVENT = 'intro:complete';
const LOGO_SRC = '/assets/images/Openbooq_blue-1773069231613.png';
const LOGO_PRELOAD_MAX_WAIT_MS = 2500;

const LOGO_FADE_IN_END_MS = 1344;
const HOLD_END_MS = 2744;
const TOTAL_DURATION_MS = 4144;

const IntroAnimation = () => {
  // HYDRATION-SAFE session gate. The homepage is prerendered at build time
  // (scripts/prerender.mjs) and hydrated with React 18 hydrateRoot — the
  // first client render MUST match the server HTML, and the server always
  // renders "no intro". So the sessionStorage + reduced-motion decision runs
  // in a mount effect (never during render): first paint shows the page
  // content, then the intro mounts one tick later for first-in-session
  // visitors. Returning visitors still bail without any image fetch.
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(INTRO_SESSION_KEY) === 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        sessionStorage.setItem(INTRO_SESSION_KEY, 'true');
        return;
      }
      setIsVisible(true);
    } catch (e) {
      // Storage blocked (private mode) -> skip the intro rather than crash.
    }
  }, []);

  const [imageReady, setImageReady] = useState(false);

  // Preload the logo so we don't kick off the animation before the visible
  // content is ready. Fail-open on error; defensive 2500ms cap.
  useEffect(() => {
    if (!isVisible) return undefined;

    let cancelled = false;
    const markReady = () => { if (!cancelled) setImageReady(true); };

    const img = new Image();
    img.onload = markReady;
    img.onerror = markReady;
    img.src = LOGO_SRC;

    const fallback = setTimeout(markReady, LOGO_PRELOAD_MAX_WAIT_MS);

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
      clearTimeout(fallback);
    };
  }, [isVisible]);

  // Unified dismiss path — used by both the natural animation completion
  // (onAnimationComplete) and the ESC key escape hatch. Sets the session
  // flag, fires the 'intro:complete' event (HeroSection.jsx listens), then
  // unmounts. Idempotent because setIsVisible(false) on an already-hidden
  // component is a no-op.
  const dismiss = useCallback(() => {
    sessionStorage.setItem(INTRO_SESSION_KEY, 'true');
    window.dispatchEvent(new Event(INTRO_COMPLETE_EVENT));
    setIsVisible(false);
  }, []);

  // ESC handler only — onAnimationComplete handles the natural end of the
  // timeline, so no setTimeout-based dismiss is needed.
  useEffect(() => {
    if (!isVisible || !imageReady) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isVisible, imageReady, dismiss]);

  if (!isVisible) return null;

  // Pre-image-ready: hold a white-screen state with no animation. Briefly
  // visible on cold loads before the logo PNG arrives. Capped at
  // LOGO_PRELOAD_MAX_WAIT_MS so a stalled network never blocks the user.
  if (!imageReady) {
    return (
      <div
        className="fixed inset-0 bg-white"
        style={{ zIndex: 2147483647 }}
        aria-hidden="true"
      />
    );
  }

  return (
    <motion.div
      // .intro-splash class drives the mask-image with --reveal (see tailwind.css).
      // The outer wrapper holds opaque white from t=0 to HOLD_END_MS, then
      // --reveal animates 0% → 120% over the reveal phase, opening a
      // transparent iris that exposes the homepage underneath.
      className="intro-splash fixed inset-0 bg-white overflow-hidden"
      style={{ zIndex: 2147483647 }}
      aria-hidden="true"
      initial={{ '--reveal': '0%' }}
      animate={{ '--reveal': ['0%', '0%', '120%'] }}
      transition={{
        duration: TOTAL_DURATION_MS / 1000,
        times: [0, HOLD_END_MS / TOTAL_DURATION_MS, 1],
        ease: [0.22, 1, 0.36, 1],
      }}
      onAnimationComplete={dismiss}
    >
      {/* Logo block — same combined PNG the site header uses. Slides up
          from below the viewport, decelerating with ease-out into its
          centered resting position; a 6px → 0px motion blur clears in sync
          so the logo appears to come into focus as it lands. */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center px-6"
        initial={{ y: '60vh', opacity: 0, filter: 'blur(6px)' }}
        animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: LOGO_FADE_IN_END_MS / 1000, ease: 'easeOut' }}
      >
        <img
          src={LOGO_SRC}
          alt="OpenbooQ brand logo featuring modern 3D interlocking geometric design with circular cylindrical shapes in gradient blue, cyan and gray tones"
          className="h-32 md:h-48 w-auto object-contain"
        />
      </motion.div>
    </motion.div>
  );
};

export default IntroAnimation;
